// src/hooks/useGroupsManager.js
import { useState, useCallback } from "react";
import { autoFillGroups, autoLabelGroups } from "../utils/groupUtils";

export function useGroupsManager(initialGroups = []) {
  const [groups, setGroups] = useState(initialGroups);

  const addFilesToGroup = useCallback((groupId, fileIds) => {
    const filesToAdd = Array.isArray(fileIds) ? fileIds : [fileIds];

    setGroups((prevGroups) => {
      // Check if the group exists, if not create it
      const groupExists = prevGroups.find((group) => group.id === groupId);
      let updatedGroups = prevGroups;

      if (!groupExists) {
        // Extract group number from groupId (e.g., "group9" -> 9)
        const groupNumber = parseInt(groupId.replace("group", ""));
        const newGroup = {
          id: groupId,
          name: `Group ${groupNumber}`,
          files: [],
        };
        updatedGroups = [...prevGroups, newGroup];
      }

      return updatedGroups.map((group) => {
        if (group.id === groupId) {
          const existingFiles = new Set(group.files || []);
          filesToAdd.forEach((fileId) => existingFiles.add(fileId));
          return { ...group, files: Array.from(existingFiles) };
        } else {
          const filteredFiles = (group.files || []).filter(
            (fileId) => !filesToAdd.includes(fileId)
          );
          return { ...group, files: filteredFiles };
        }
      });
    });
  }, []);

  const removeFilesFromGroup = useCallback((groupId, fileIds) => {
    const filesToRemove = Array.isArray(fileIds) ? fileIds : [fileIds];

    setGroups((prevGroups) => {
      // Only process if the group exists
      const groupExists = prevGroups.find((group) => group.id === groupId);
      if (!groupExists) {
        console.warn(`Group ${groupId} does not exist for file removal`);
        return prevGroups;
      }

      return prevGroups.map((group) => {
        if (group.id === groupId) {
          const filteredFiles = (group.files || []).filter(
            (fileId) =>
              !filesToRemove.some(
                (removeId) => String(fileId) === String(removeId)
              )
          );
          return { ...group, files: filteredFiles };
        }
        return group;
      });
    });
  }, []);

  const updateGroupName = useCallback((groupId, newName) => {
    setGroups((prevGroups) => {
      // Check if the group exists, if not create it
      const groupExists = prevGroups.find((group) => group.id === groupId);
      let updatedGroups = prevGroups;

      if (!groupExists) {
        // Extract group number from groupId (e.g., "group9" -> 9)
        const groupNumber = parseInt(groupId.replace("group", ""));
        const newGroup = {
          id: groupId,
          name: newName,
          files: [],
        };
        updatedGroups = [...prevGroups, newGroup];
      }

      return updatedGroups.map((group) =>
        group.id === groupId ? { ...group, name: newName } : group
      );
    });
  }, []);

  const clearGroup = useCallback((groupId) => {
    setGroups((prevGroups) => {
      // Only process if the group exists
      const index = prevGroups.findIndex(g => g.id === groupId);
      if (index === -1) {
        console.warn(`Group ${groupId} does not exist for clearing`);
        return prevGroups;
      }

      updateGroupName(groupId, `Group ${index + 1}`); // Reset to default name

      return prevGroups.map((group) =>
        group.id === groupId ? { ...group, files: [] } : group
      );

    });
  }, []);

  const clearAllGroups = useCallback(() => {
    setGroups((prevGroups) => {
      return prevGroups.map((group, index) => ({
        ...group,
        files: [],
        name: `Group ${index + 1}`, // Reset to default name
      }));
    });
  }, []);

  const handleAutoFill = useCallback((displayedFiles, allFiles) => {
    setGroups((prevGroups) => {
      const filledGroups = autoFillGroups(displayedFiles, prevGroups);
      return autoLabelGroups(filledGroups, allFiles);
    });
  }, []);

  const handleAutoLabel = useCallback((allFiles) => {
    if (!allFiles || allFiles.length === 0) {
      return;
    }

    setGroups((prevGroups) => {
      return autoLabelGroups(prevGroups, allFiles);
    });
  }, []);

  const resetGroups = useCallback((newGroups) => {
    setGroups(newGroups);
  }, []);

  return {
    groups,
    addFilesToGroup,
    removeFilesFromGroup,
    updateGroupName,
    clearGroup,
    clearAllGroups,
    handleAutoFill,
    handleAutoLabel,
    resetGroups,
    setGroups,
  };
}
